import * as z from 'zod';
import {organizationRefSchema, readOrganizationRefSchema} from '../organizations';
import {audienceRefSchema, readAudienceSchema} from "../audiences";
import {readLeadSchema} from "../leads";
import {readBrandRefSchema} from "../brands";
import {jobStatisticsSchema, readJobSchema} from "../jobs";
import {blockTypeSchema, cssSelectorMapSchema, readTrackedEventSchema, timeSeriesSchema} from "../common";
import {embeddedBlockVariantSchema} from "../block-variants";
import {readLandingPageSchema} from "../landing-pages";

// Enums
export const campaignStatusEnum = z.enum(['draft', 'ready', 'published', 'unpublished']);
export type CampaignStatus = z.infer<typeof campaignStatusEnum>;

// Whether the campaign's generated pages are stale relative to its inputs.
// Same semantics as pageOutdatedStatus (../pages/models.ts) — inlined here to
// avoid the campaigns↔pages circular import.
export const campaignOutdatedStatusEnum = z.enum(['none', 'needs_regeneration', 'needs_republication']);
export type CampaignOutdatedStatus = z.infer<typeof campaignOutdatedStatusEnum>;

export const targetingApproachEnum = z.enum(['INDUSTRY', 'JOB_TITLE', 'BOTH', 'OTHER']);
export type TargetingApproach = z.infer<typeof targetingApproachEnum>;

// Client-writable wizard intent: whether the campaign starts from a blank
// (Tinct-generated) page or personalizes one of the customer's existing pages.
export const landingPageOriginTypeEnum = z.enum(['new', 'existing']);
export type LandingPageOriginType = z.infer<typeof landingPageOriginTypeEnum>;

// What the campaign's landing-page variations are declined along: one per ad
// keyword (search intent, a "Google Ads campaign") or one per targeted
// company. Client-writable wizard intent, set at creation; values pass
// verbatim on the wire.
export const landingPageVariationAxisEnum = z.enum(['adword', 'company_list']);
export type LandingPageVariationAxis = z.infer<typeof landingPageVariationAxisEnum>;

// CampaignContext schema (embedded JSONB object)
export const campaignContextSchema = z.object({
  valueProposition: z.string().nullable(),
  goal: z.string().nullable(),
  targetingApproach: targetingApproachEnum.nullable(),
  targetIndustries: z.array(z.string()).nullable(),
  targetJobTitles: z.array(z.string()).nullable(),
  targetCompanySize: z.string().nullable(),
  keyMessage: z.string().nullable(),
  differentiation: z.string().nullable(),
  tone: z.string().nullable(),
  keywords: z.array(z.string()).nullable(),
});
export type CampaignContext = z.infer<typeof campaignContextSchema>;

// Create/Update CampaignContext schema (all fields optional)
export const updateCampaignContextSchema = z.object({
  valueProposition: z.string().optional(),
  goal: z.string().optional(),
  targetingApproach: targetingApproachEnum.optional(),
  targetIndustries: z.array(z.string()).optional(),
  targetJobTitles: z.array(z.string()).optional(),
  targetCompanySize: z.string().optional(),
  keyMessage: z.string().optional(),
  differentiation: z.string().optional(),
  tone: z.string().optional(),
  keywords: z.array(z.string()).optional(),
});
export type UpdateCampaignContext = z.infer<typeof updateCampaignContextSchema>;

// Block schema (for embedding in Campaign)
// Note: This is a simplified version for use within Campaign responses.
// `blockTypeSchema` lives in ../common (shared by Block/BlockVariant DTOs).
export const embeddedBlockSchema = z.object({
  id: z.uuid().nullable(),
  cssSelectors: cssSelectorMapSchema.nullable(),
  value: z.string().nullable(),
  valueAsText: z.string().nullable(),
  enabled: z.boolean().nullable(),
  status: z.string().nullable(),
  type: blockTypeSchema.nullable(),
  sectionCssSelector: z.string().nullable(),
  label: z.string().nullable(),
  track: z.boolean().nullable(),
  eventName: z.string().nullable(),
  priority: z.number().nullable(),
  // Render logos in greyscale. Relevant when `type` is `customer_logo`;
  // null/unused for other block types.
  greyscale: z.boolean().nullable(),
  // Render logos using the dark-theme variant. Relevant when `type` is
  // `customer_logo`; null/unused for other block types. Combines with
  // `greyscale` to select which company logo variant the edge renders.
  dark: z.boolean().nullable(),
  // Render logos at the larger size variant. Relevant when `type` is
  // `customer_logo`; null/unused for other block types. Pure passthrough
  // carried to the Cloudflare edge for the SDK to size the logo component.
  largeLogo: z.boolean().nullable(),
  // Recolor logos client-side into a flat silhouette of this hex color
  // (e.g. "#FFFFFF"), for colored/photo backgrounds. Relevant when `type` is
  // `customer_logo`; null/unused for other block types. Overrides
  // `greyscale`/`dark` — the silhouette is rendered from any available base
  // logo variant.
  logoFillColor: z.string().nullable(),
  // Number of <img> logo slots in the originally captured carousel markup.
  // Relevant when `type` is `customer_logo`; read-only, measured once at
  // block-identification time. 0 = captured content has no <img> (e.g.
  // inline-SVG carousel); null = no content captured, or a block identified
  // before this field existed (no backfill).
  logoCount: z.number().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type EmbeddedBlock = z.infer<typeof embeddedBlockSchema>;

// Page schema (for embedding in Campaign)
// Note: This is a simplified version for use within Campaign responses
export const embeddedPageSchema = z.object({
  id: z.uuid().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  lead: z.lazy(() => readLeadSchema).nullable(),
  // Whether this variant targets a lead (company) or an ad campaign (Google
  // Ads or LinkedIn Ads).
  targetType: z.enum(['lead', 'ad_campaign']).nullable(),
  // Minimal ads-campaign shape, inlined to avoid the campaigns↔crm-integrations
  // circular import (full shape: readAdsCampaignSchema). `integrationId`
  // resolves the provider through the organization's integrations.
  adCampaign: z
    .object({
      id: z.uuid().nullable(),
      integrationId: z.uuid().nullable(),
      name: z.string().nullable(),
      externalCampaignId: z.string().nullable(),
    })
    .nullable(),
  blockVariants: z.array(z.lazy(() => embeddedBlockVariantSchema)).nullable(),
  lastGenerationJob: z.lazy(() => readJobSchema).nullable(),
  status: z.enum(['draft', 'published', 'unpublished', 'waiting', 'queued', 'generating', 'ready', 'error', 'excluded', 'excluded_conflict']),
  // Mirrors pageOutdatedStatus in ../pages/models.ts (inlined to avoid the campaigns↔pages circular import).
  outdatedStatus: z.enum(['none', 'needs_regeneration', 'needs_republication']).nullable(),
  errorCode: z.string().nullable(),
});
export type EmbeddedPage = z.infer<typeof embeddedPageSchema>;

// Campaign snippet status schema (read-only, system-managed)
export const campaignSnippetStatusEnum = z.enum(['active', 'not_detected', 'on_error']);
export type CampaignSnippetStatus = z.infer<typeof campaignSnippetStatusEnum>;

export const campaignSnippetStatusSchema = z.object({
  status: campaignSnippetStatusEnum.nullable(),
  error: z.string().nullable(),
  verifiedAt: z.iso.datetime().nullable(),
});
export type ReadCampaignSnippetStatus = z.infer<typeof campaignSnippetStatusSchema>;

// Campaign statistics schema
export const campaignStatisticsSchema = z.object({
  blocksCount: z.number().nullable(),
  totalPagesCount: z.number().nullable(),
  excludedPagesCount: z.number().nullable(),
  publishedPagesCount: z.number().nullable(),
  leadsCount: z.number().nullable(),
  visitsCount: z.number().nullable(),
  // Every analytics event ever received (all types, no period). Non-zero means
  // the campaign was served to visitors and can no longer be deleted.
  eventsCount: z.number().nullable(),
  periodVisitsVariationInPercent: z.number().nullable(),
  sessionAverageTimeInSeconds: z.number().nullable(),
  periodSessionAverageTimeVariationInPercent: z.number().nullable(),
  originalSessionAverageTimeVariationInPercent: z.number().nullable(),
  ctaClickPerSession: z.number().nullable(),
  periodCtaClickPerSessionVariationInPercent: z.number().nullable(),
  originalCtaClickPerSessionVariationInPercent: z.number().nullable(),
  visitsTimeSeries: timeSeriesSchema.nullable(),
  originalVisitsTimeSeries: timeSeriesSchema.nullable(),
  trackedEvents: z.array(readTrackedEventSchema).nullable(),
  generationJobsStatistics: jobStatisticsSchema.nullable(),
});
export type CampaignStatistics = z.infer<typeof campaignStatisticsSchema>;

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readCampaignRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});
export type ReadCampaignRef = z.infer<typeof readCampaignRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const campaignRefSchema = z.object({
  id: z.uuid(),
});
export type CampaignRef = z.infer<typeof campaignRefSchema>;


export const campaignStepEnumSchema = z.enum(['value_proposition', 'block_identification', 'audience', 'validation']);

export type CampaignStepEnum = z.infer<typeof campaignStepEnumSchema>;

// 3. Read schema - all properties nullable (API response)
export const readCampaignSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().nullable(),
  // The landing page this campaign personalizes (any origin). Null until the
  // campaign has a URL/page linked — the old `landingPageUrl` null-checks move
  // one level up (`campaign.landingPage?.url`).
  landingPage: z.lazy(() => readLandingPageSchema).nullable(),
  landingPageOriginType: landingPageOriginTypeEnum.nullable(),
  landingPageVariationAxis: landingPageVariationAxisEnum.nullable(),
  originalPageSignedUrl: z.string().nullable(),
  organization: z.lazy(() => readOrganizationRefSchema).nullable(),
  published: z.boolean().nullable(),
  status: campaignStatusEnum.nullable(),
  outdatedStatus: campaignOutdatedStatusEnum.nullable(),
  // Pages `_regenerate-outdated` would submit right now (flagged needs_regeneration,
  // generated, no error, no job in flight). Computed server-side; the number of
  // generation credits the automatic regeneration is about to spend.
  pagesToRegenerateCount: z.number().nullable(),
  campaignContext: campaignContextSchema.nullable(),
  // Editorial rules the backend learned from the user's page edits, grouped per block
  // (`Block label:` headings, `- rule` lines). Read-only: never part of update/patch payloads.
  customInstructions: z.string().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  lastPublishedAt: z.iso.datetime().nullable(),
  firstPublishedAt: z.iso.datetime().nullable(),
  blocks: z.array(embeddedBlockSchema).nullable(),
  pages: z.array(embeddedPageSchema).nullable(),
  audience: z.lazy(() => readAudienceSchema).nullable(),
  brand: z.lazy(() => readBrandRefSchema).nullable(),
  lastAnalysisJob: z.lazy(() => readJobSchema).nullable(),
  lastPublishedJob: z.lazy(() => readJobSchema).nullable(),
  lastUnpublishedJob: z.lazy(() => readJobSchema).nullable(),
  confirmedStatus: z.boolean().nullable(),
  confirmedSteps: z.array(campaignStepEnumSchema).nullable(),
  samplePages: z.array(embeddedPageSchema).nullable(),
  statistics: campaignStatisticsSchema.nullable(),
  snippetStatus: campaignSnippetStatusSchema.nullable(),
});
export type ReadCampaign = z.infer<typeof readCampaignSchema>;

// Write-side reference to the campaign's landing page. Send exactly one of
// `url` (backend finds or creates the organization's existing-origin page for
// that URL) or `id` (link an existing page, e.g. a hosted one — an unknown or
// other-organization id is a 400). Any other sub-field sent is ignored.
// `null` on PUT unlinks the page.
export const campaignLandingPageLinkSchema = z.object({
  id: z.uuid().optional(),
  url: z
    .string()
    .min(1, 'Landing page URL is required')
    .max(2000, 'Landing page URL must not exceed 2000 characters')
    .optional(),
});
export type CampaignLandingPageLink = z.infer<typeof campaignLandingPageLinkSchema>;

// 4. Create schema - based on DTO serialization groups
export const createCampaignSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(250, 'Name must not exceed 250 characters')
    .optional(),
  landingPage: campaignLandingPageLinkSchema.nullable().optional(),
  landingPageOriginType: landingPageOriginTypeEnum.optional(),
  landingPageVariationAxis: landingPageVariationAxisEnum.optional(),
  organization: z.lazy(() => organizationRefSchema),
  campaignContext: updateCampaignContextSchema.optional(),
  audience: z.lazy(() => audienceRefSchema).nullable().optional(),
});
export type CreateCampaign = z.infer<typeof createCampaignSchema>;

// 5. Update schema - based on DTO serialization groups
export const updateCampaignSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(250, 'Name must not exceed 250 characters')
    .optional(),
  landingPage: campaignLandingPageLinkSchema.nullable().optional(),
  landingPageOriginType: landingPageOriginTypeEnum.optional(),
  landingPageVariationAxis: landingPageVariationAxisEnum.optional(),
  blocks: z.array(embeddedBlockSchema).optional(),
  status: campaignStatusEnum.optional(),
  campaignContext: updateCampaignContextSchema.optional(),
  audience: z.lazy(() => audienceRefSchema).nullable().optional(),
});
export type UpdateCampaign = z.infer<typeof updateCampaignSchema>;

// 6. Patch schema - partial update
export const patchCampaignSchema = updateCampaignSchema.partial();
export type PatchCampaign = z.infer<typeof patchCampaignSchema>;

// Legacy exports for backward compatibility
export const campaignSchema = readCampaignSchema;
export type Campaign = ReadCampaign;

export type AudienceConflictDto = {
  company: { id: string; name: string; logo?: { url: string } | null };
  campaign: { id: string; name: string };
};

export type GenerationResultDto = {
  jobsSubmitted: number;
  jobsSkipped: number;
  conflicts?: AudienceConflictDto[] | null;
}

// Result of cancelling a campaign's pending generation: only QUEUED jobs can be
// cancelled (a RUNNING generation finishes), so this counts the pages that went
// back to waiting.
export type CancelGenerationResultDto = {
  jobsCancelled: number;
};

// Body of the logo re-match action: after a removal, the company that left the
// library narrows the work to the pages showing it (and makes the call a no-op
// when none does). Omitted after an addition.
export type LogoRematchRequestDto = {
  removedCompanyId?: string;
};

// Result of the logo re-match action: the job rewriting the CUSTOMER_LOGO
// selections in the background (poll it), or a null jobId when nothing needed
// re-matching — no logo block, no generated page, or a removed logo no page showed.
export type LogoRematchResultDto = {
  jobId: string | null;
  pagesToRematch: number;
};

// Non-mutating preview of the impact of switching a campaign to a different
// audience, returned by the preview-audience-change endpoint. Lets the UI warn
// the user before the change is applied (and pages are archived/regenerated).
// The current/new audience identities are known client-side, so only the diff
// counts are returned here.
export type AudienceChangeDto = {
  // Companies present in both audiences — their pages will be kept.
  keptCompaniesCount: number;
  // Companies only in the current audience — their pages will be archived.
  removedCompaniesCount: number;
  // Companies only in the new audience — pages will be generated for them.
  newCompaniesCount: number;
  // Companies in the new audience already targeted by another campaign on the
  // same page — they are in conflict and their pages won't be generated.
  conflictCompaniesCount: number;
};
